import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight as ChevronRightIcon,
  ArrowBackOutlined as ArrowBackOutlinedIcon,
  ScienceOutlined as ScienceOutlinedIcon,
} from '@mui/icons-material';
import { INDIGO, SLATE } from '../../constants/Colors';

function InSyncLogo() {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #4f46e5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>AI</Typography>
    </Box>
  );
}

function Breadcrumb({ appName, agentName }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '2px' }}>
      <Typography sx={{ fontSize: '0.75rem', color: SLATE[500] }}>Creating for:</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: '6px',
            py: '2px',
            borderRadius: '6px',
            border: `1px solid ${SLATE[200]}`,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '3px',
              bgcolor: SLATE[300],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>A</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: SLATE[700], fontWeight: 500 }}>
            {appName}
          </Typography>
        </Box>

        <ChevronRightIcon sx={{ fontSize: '0.875rem', color: SLATE[400] }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: '6px',
            py: '2px',
            borderRadius: '6px',
            border: `1px solid ${SLATE[200]}`,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '3px',
              bgcolor: INDIGO[400],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>A</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: SLATE[700], fontWeight: 500 }}>
            {agentName || 'Agent'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function ChatHeader({ appName, agentName, onBuildManually, agentId }) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.5,
        bgcolor: '#fff',
        borderBottom: `1px solid ${SLATE[200]}`,
        flexShrink: 0,
      }}
    >
      {/* Left — logo + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <InSyncLogo />
        <Box>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: INDIGO[600],
              lineHeight: 1.2,
            }}
          >
            InSyncAI Studio
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: SLATE[500], lineHeight: 1.2 }}>
            AI Agent Builder
          </Typography>
          <Breadcrumb appName={appName} agentName={agentName} />
        </Box>
      </Box>

      {/* Right — action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Back to Home" placement="bottom" arrow>
          <Box
            onClick={onBuildManually}
            sx={{
              display: 'flex', alignItems: 'center', gap: '5px',
              px: '12px', height: 38, borderRadius: '10px',
              border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
              cursor: 'pointer', overflow: 'hidden',
              transition: 'background 0.15s', '&:hover': { bgcolor: SLATE[50] },
            }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: '1.1rem', color: SLATE[600], flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
              Back to Home
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="⚙️ Configure Manually" placement="bottom" arrow>
          <Box
            onClick={onBuildManually}
            sx={{
              display: 'flex', alignItems: 'center', gap: '5px',
              px: '12px', height: 38, borderRadius: '10px',
              border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
              cursor: 'pointer', overflow: 'hidden',
              transition: 'background 0.15s', '&:hover': { bgcolor: SLATE[50] },
            }}
          >
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}>⚙️</Typography>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
              Configure Manually
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Test Agent" placement="bottom" arrow>
          <Box
            onClick={() => navigate(`/${appName}/agent/${agentId}/settings/bot-testing`)}
            sx={{
              display: 'flex', alignItems: 'center', gap: '5px',
              px: '12px', height: 38, borderRadius: '10px',
              border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
              cursor: 'pointer', overflow: 'hidden',
              transition: 'background 0.15s', '&:hover': { bgcolor: SLATE[50] },
            }}
          >
            <ScienceOutlinedIcon sx={{ fontSize: '1.1rem', color: SLATE[600], flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
              Test Agent
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
